import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, MenuItem, Tooltip, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {DeleteOutline, Edit, RateReview, Visibility} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import useSearch from "../../../../../hooks/use-search";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {useBizFormApiResolver} from "../../../../../hooks/use-biz-form-api-resolver";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import IconButton from "../../../../../components/base/button/icon_button";
import Button from "../../../../../components/base/button/button";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import OperatorAutocomplete from "../../../../../components/text-fields/autocomplete/operator-autocomplete";
import CustomerAutocomplete from "../../../../../components/text-fields/autocomplete/customer-autocomplete";
import ViewFormResponseDialog from "../../../../../components/dialogs/view-form-response";
import ReviewFormResponseDialog from "../../../../../components/dialogs/review-form-response";
import AnswerFormDialog from "../../../../../components/dialogs/answer-form-dialog";
import DateUtils from "../../../../../../core/services/date-utils";
import Form, {MuiRFF, Yup} from "../../../../../components/form";
import FormsAutocomplete from "../../../../../components/text-fields/autocomplete/forms-autocomplete";
import useQuery from "../../../../../hooks/use-query";
import {FormResponseStatuses, FormResponseStatusFilterNone, FormResponseStatusList,} from "../../../../../../packages/biz-form/core/constants/form-response-statuses";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {useBizFileUrlsResolver} from "../../../../../hooks/use-biz-file-urls-resolver";

const dataGridSaveConfig = {
    name: "FormsManagementResponsesDataGrid",
    version: 2,
};

const FormResponsesViewDataGridColumnNames = {
    actions: "actions",
    formTitle: "formTitle",
    createdByName: "createdByName",
    externalUserName: "externalUserName",
    createdDateTime: "createdDateTime",
    status: "status",
    reviewComment: "reviewComment",
    statusExpiry: "statusExpiry",
    statusUpdateDateTime: "statusUpdateDateTime",
    changedStatusByName: "changedStatusByName",
};

const getStatusColor = (status) => {
    switch (status) {
        case FormResponseStatuses.Approved:
            return "success.main";
        case FormResponseStatuses.Pending:
            return "warning.main";
        case FormResponseStatuses.Declined:
            return "error.main";
        default:
            return "text.secondary";
    }
};

const mapStatusFiltersForApi = (statuses) =>
    statuses?.map((status) => (status === FormResponseStatusFilterNone ? null : status));

const renderStatusChip = (status, statusLabel) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 1, justifyContent: "center"}}>
        <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: getStatusColor(status)}}/>
        {statusLabel}
    </Box>
);

const FormResponsesView = ({fixedCustomerId, pageTitle, notebookTagConfigurator}) => {
    const {t} = useTranslation("views", {keyPrefix: "forms-management.responses"});
    const isMounted = useIsMounted();
    const [query] = useQuery();
    const dispatch = useDispatch();
    const operatorAccessLevels = useAccessLevels();
    const {configureNotebookTags} = useConfigureNotebook("forms-management-responses");
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [data, setData] = useState([]);
    const [viewDialog, setViewDialog] = useState({open: false, formId: null, responseId: null});
    const [reviewDialog, setReviewDialog] = useState({open: false, response: null});
    const [answerFormDialog, setAnswerFormDialog] = useState({open: false, formId: null, responseId: null});

    const bizFormApiClient = useBizFormApiResolver((t) => t.operatorApp);
    const bizFileUrlChannel = useBizFileUrlsResolver((t) => t.bizFile);

    /**
     * Resolves preview URL for a given file GUID using operatorBizFile from useBizFileUrlsResolver.
     */
    const getFilePreviewUrl = useCallback((guid) => bizFileUrlChannel.getFilePreviewUrl(guid ?? ""), [bizFileUrlChannel]);

    const canManage = operatorAccessLevels.packages.bizForm.formResponsesManage;

    /**
     * Configures notebook tags and behavioral analytics tool for the form responses view.
     */
    useEffect(() => {
        const tagFn = notebookTagConfigurator ?? ((noteTags) => noteTags.formsManagementResponses);
        configureNotebookTags(tagFn);
        configureBehavioralTool((tags) => tags.formsManagementResponses);
    }, [configureBehavioralTool, configureNotebookTags, notebookTagConfigurator]);

    const columns = useMemo(() => [
        {
            name: FormResponsesViewDataGridColumnNames.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: canManage ? 120 : 0,
        },
        {
            title: t("columns.formTitle"),
            name: FormResponsesViewDataGridColumnNames.formTitle,
            type: DataGridColumnTypes.string,
        },
        {
            title: t("columns.createdByName"),
            name: FormResponsesViewDataGridColumnNames.createdByName,
            type: DataGridColumnTypes.string,
        },
        {
            title: t("columns.externalUserName"),
            name: FormResponsesViewDataGridColumnNames.externalUserName,
            type: DataGridColumnTypes.string,
        },
        {
            title: t("columns.createdDateTime"),
            name: FormResponsesViewDataGridColumnNames.createdDateTime,
            type: DataGridColumnTypes.dateTime,
            width: 140,
        },
        {
            title: t("columns.status"),
            name: FormResponsesViewDataGridColumnNames.status,
            type: DataGridColumnTypes.element,
            sortable: false,
            width: 120,
        },
        {
            title: t("columns.reviewComment"),
            name: FormResponsesViewDataGridColumnNames.reviewComment,
            type: DataGridColumnTypes.element,
            sortable: false,
            width: 180,
        },
        {
            title: t("columns.statusExpiry"),
            name: FormResponsesViewDataGridColumnNames.statusExpiry,
            type: DataGridColumnTypes.element,
            sortable: false,
            width: 160,
        },
        {
            title: t("columns.statusUpdateDateTime"),
            name: FormResponsesViewDataGridColumnNames.statusUpdateDateTime,
            type: DataGridColumnTypes.dateTime,
            width: 140,
        },
        {
            title: t("columns.changedStatusByName"),
            name: FormResponsesViewDataGridColumnNames.changedStatusByName,
            type: DataGridColumnTypes.string,
            width: 140,
        },
    ], [canManage, t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo, [dataGridPageSize]);

    /**
     * Performs a search for form responses based on the provided arguments.
     */
    const search = useCallback(async (args) => {
        const formIds = args.filters?.forms?.map((e) => JSON.parse(e).id).filter(Boolean);
        const reviewerIds = args.filters?.reviewers?.map((e) => JSON.parse(e).id).filter(Boolean);
        const customerIds = args.filters?.customers?.map((e) => JSON.parse(e).id).filter(Boolean);
        const forApi = {
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{property: args.orderBy.field, isDesc: args.orderBy.descending}] : [],
            keyword: args.filters?.keyword ?? "",
            ...(formIds?.length && {formIds}),
            ...(args.filters?.statuses?.length && {statuses: mapStatusFiltersForApi(args.filters.statuses)}),
            ...(customerIds?.length && {customerIds}),
            ...(reviewerIds?.length && {reviewerIds}),
            ...(args.filters?.expiryAfter && {expiryAfterDateTime: args.filters.expiryAfter}),
            ...(args.filters?.expiryBefore && {expiryBeforeDateTime: args.filters.expiryBefore}),
        };
        const response = await bizFormApiClient.listFormResponses(forApi);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag && response.data) {
            setData(response.data.items ?? []);
            args.setPaginationInfo((prev) => ({
                ...prev,
                length: response.data.totalCount ?? 0,
            }));
        } else {
            setData([]);
        }
    }, [bizFormApiClient, isMounted]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {
            keyword: "",
            statuses: [],
            ...(query?.form && {
                forms: [query.form],
            }),
            ...(fixedCustomerId && {
                customers: [JSON.stringify({id: fixedCustomerId})],
            }),
        },
    }), [dataGridSortBy, fixedCustomerId, initialPaginationInfo, query?.form]);

    const searchData = useSearch(search, initialSearchDataSettings, true);

    /**
     * Opens a confirmation dialog to remove a form response.
     */
    const openRemove = useCallback((row) => {
        dispatch(
            setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
                open: true,
                data: null,
                removedEntryTitle: t("deleteConfirmation", {formTitle: row.formTitle}),
                onClose: async (confirmed) => {
                    if (!confirmed) {
                        return false;
                    }
                    const del = await bizFormApiClient.deleteFormResponse(row.id);
                    if (del?.resultFlag) {
                        await searchData.performSearch();
                    }
                    return del?.resultFlag ?? false;
                },
            }),
        );
    }, [bizFormApiClient, dispatch, searchData, t]);

    const getStatusLabel = useCallback((status) => {
        if (status == null || status === "") {
            return t("statuses.notProvided");
        }
        const key = `statuses.${status}`;
        const translated = t(key);
        return translated === key ? status : translated;
    }, [t]);

    const rows = useMemo(() => {
        return data.map((e) => {
            const isExpired = e.statusExpiry && DateUtils.localNow().isSameOrAfter(DateUtils.serverToLocal(e.statusExpiry));
            const isExternalSubmission = Boolean(e.externalUserId);
            return ({
                key: e.id,
                data: e,
                cells: {
                    [FormResponsesViewDataGridColumnNames.formTitle]: e.formTitle,
                    [FormResponsesViewDataGridColumnNames.createdByName]: e.createdByName ?? "",
                    [FormResponsesViewDataGridColumnNames.externalUserName]: e.externalUserName || t("notAvailable"),
                    [FormResponsesViewDataGridColumnNames.createdDateTime]: e.createdDateTime,
                    [FormResponsesViewDataGridColumnNames.status]: renderStatusChip(e.status, getStatusLabel(e.status)),
                    [FormResponsesViewDataGridColumnNames.reviewComment]:
                        <Tooltip title={e.reviewComment ?? ""} placement="bottom" arrow disableHoverListener={!e.reviewComment}>
                            <Typography {...dataGridCellTextProps} variant="body2" sx={{maxWidth: 180}}>
                                {e.reviewComment ?? ""}
                            </Typography>
                        </Tooltip>,
                    [FormResponsesViewDataGridColumnNames.statusExpiry]: (
                        <Box>
                            {e.statusExpiry
                                ? DateUtils.serverToLocal(e.statusExpiry).format(DateUtils.dateTimeFormat)
                                : ""}
                            {isExpired && (
                                <Typography variant="body2" color="error.main" fontWeight={600}>
                                    {t("expired")}
                                </Typography>
                            )}
                        </Box>
                    ),
                    [FormResponsesViewDataGridColumnNames.statusUpdateDateTime]: e.statusUpdateDateTime,
                    [FormResponsesViewDataGridColumnNames.changedStatusByName]: e.changedStatusByName ?? "",
                    [FormResponsesViewDataGridColumnNames.actions]: (
                        <Box sx={{display: "flex", gap: 0.5, justifyContent: "center"}}>
                            {canManage && (
                                <>
                                    <Tooltip title={!isExternalSubmission ? t("actions.edit") : ""}>
                                        <IconButton
                                            color="paper.secondary"
                                            size="small"
                                            onPressed={() => setAnswerFormDialog({open: true, formId: e.formId, responseId: e.id})}
                                            aria-label={t("actions.edit")}
                                            sx={{
                                                ...(isExternalSubmission && {
                                                    visibility: 'hidden',
                                                })
                                            }}
                                        >
                                            <Edit className="icon small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("actions.delete")}>
                                        <IconButton
                                            color="paper.error"
                                            size="small"
                                            onPressed={() => openRemove(e)}
                                            aria-label={t("actions.delete")}
                                        >
                                            <DeleteOutline className="icon small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("actions.review")}>
                                        <IconButton
                                            color="paper.secondary"
                                            size="small"
                                            onPressed={() => setReviewDialog({open: true, response: e})}
                                            aria-label={t("actions.review")}
                                        >
                                            <RateReview className="icon small"/>
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                            <Tooltip title={t("actions.view")}>
                                <IconButton
                                    color="paper.primary"
                                    size="small"
                                    onPressed={() => setViewDialog({open: true, formId: e.formId, responseId: e.id})}
                                    aria-label={t("actions.view")}
                                >
                                    <Visibility className="icon small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ),
                },
            });
        });
    }, [canManage, data, getStatusLabel, openRemove, t]);

    const heading = pageTitle ?? t("pageTitle");

    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%", gap: 4}}>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", my: 2, width: "100%"}}>
                <Typography variant="h6" fontWeight={600}>
                    {heading}
                </Typography>
            </Box>
            <FormResponsesViewSearchSection
                filters={searchData.filters}
                setFilters={searchData.setFilters}
                showCustomers={!fixedCustomerId}
                api={bizFormApiClient}
            />
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                hideExporting
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) =>
                    searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))
                }
                onPageSizeChanged={(_, current) =>
                    searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))
                }
            />
            <ViewFormResponseDialog
                open={viewDialog.open}
                onClose={() => setViewDialog({open: false, formId: null, responseId: null})}
                formId={viewDialog.formId}
                responseId={viewDialog.responseId}
                apiClient={bizFormApiClient}
                getFilePreviewUrl={getFilePreviewUrl}
            />
            <ReviewFormResponseDialog
                open={reviewDialog.open}
                onClose={() => setReviewDialog({open: false, response: null})}
                response={reviewDialog.response}
                api={bizFormApiClient}
                onSuccess={() => void searchData.performSearch()}
            />
            <AnswerFormDialog
                open={answerFormDialog.open}
                onClose={() => setAnswerFormDialog({open: false, formId: null, responseId: null})}
                formId={answerFormDialog.formId}
                responseId={answerFormDialog.responseId}
                api={bizFormApiClient}
                fileInfo={bizFileUrlChannel.fileInfo}
                onSuccess={() => void searchData.performSearch()}
            />
        </Box>
    );
};

export default FormResponsesView;


const FormResponsesViewSearchSection = ({filters, setFilters, showCustomers, api}) => {
    const {t} = useTranslation("views", {keyPrefix: "forms-management.responses.search"});

    const schema = useMemo(() => Yup.object().shape({
        keyword: Yup.string().optional().nullable(),
        reviewers: Yup.array().optional().nullable(),
        customers: Yup.array().optional().nullable(),
        forms: Yup.array().optional().nullable(),
        statuses: Yup.array().optional().nullable(),
        startDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan('End Date'))
            .nullable(),
        endDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .minDate(Yup.ref('startDate'), ValidateMessages.greaterThan('Start Date'))
            .nullable(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const statusOptions = useMemo(() => [
        ...FormResponseStatusList.map((status) => ({
            value: status,
            label: t(`statusOptions.${status}`),
        })),
        {
            value: FormResponseStatusFilterNone,
            label: t("statusOptions.notProvided"),
        },
    ], [t]);

    const initialValues = useMemo(() => ({
        keyword: filters.keyword ?? "",
        reviewers: filters.reviewers ? filters.reviewers.map((e) => JSON.parse(e)) : undefined,
        customers: filters.customers ? filters.customers.map((e) => JSON.parse(e)) : undefined,
        forms: filters.forms ? filters.forms.map((e) => JSON.parse(e)) : undefined,
        statuses: filters.statuses ?? [],
        expiryAfter: filters.expiryAfter ? DateUtils.serverToUTC(filters.expiryAfter) : null,
        expiryBefore: filters.expiryBefore ? DateUtils.serverToUTC(filters.expiryBefore) : null,
    }), [filters]);

    const submitForm = useCallback((values) => {
        setFilters((prev) => ({
            ...prev,
            keyword: values.keyword,
            reviewers: values.reviewers ? values.reviewers.map((e) => JSON.stringify(e)) : undefined,
            forms: values.forms ? values.forms.map((e) => JSON.stringify(e)) : undefined,
            statuses: values.statuses?.length ? values.statuses : [],
            expiryAfter: values.expiryAfter?.isValid?.() ? DateUtils.UTCToServer(values.expiryAfter) : undefined,
            expiryBefore: values.expiryBefore?.isValid?.() ? DateUtils.UTCToServer(values.expiryBefore) : undefined,
            ...(showCustomers && {
                customers: values.customers ? values.customers.map((e) => JSON.stringify(e)) : undefined,
            }),
        }));
    }, [setFilters, showCustomers]);

    return (
        <Form
            initialValues={initialValues}
            validate={validate}
            onSubmit={submitForm}
            formProps={{className: "w-100"}}
        >
            {({values}) => (
                <Grid container spacing={2}>
                    <Grid xs={12} md={showCustomers ? 12 : true}>
                        <MuiRFF.TextField
                            name="keyword"
                            label={t("keyword")}
                            fullWidth
                            variant="filled"
                            InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                            required={required.keyword}
                        />
                    </Grid>
                    <Grid xs={12} md={showCustomers ? 6 : 'auto'}>
                        <MuiRFF.TextField
                            name="statuses"
                            label={t("statusFilter")}
                            select
                            SelectProps={{multiple: true}}
                            fullWidth
                            variant="filled"
                            InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                            sx={{
                                minWidth: 200,
                            }}
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MuiRFF.TextField>
                    </Grid>
                    {
                        !showCustomers &&
                        <Grid xs={12} sx={{marginBlock: -2}}/>
                    }
                    <Grid xs={12} md={6}>
                        <FormsAutocomplete
                            form
                            api={api}
                            name="forms"
                            label={t("forms")}
                            multiple
                            fullWidth
                            required={required.forms}
                            textFieldProps={{
                                variant: "filled",
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <OperatorAutocomplete
                            form
                            name="reviewers"
                            label={t("reviewers")}
                            multiple
                            fullWidth
                            required={required.reviewers}
                            textFieldProps={{
                                variant: "filled",
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Grid>
                    {showCustomers && (
                        <Grid xs={12} md={6}>
                            <CustomerAutocomplete
                                form
                                name="customers"
                                label={t("customers")}
                                multiple
                                fullWidth
                                required={required.customers}
                                textFieldProps={{
                                    variant: "filled",
                                    InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                                }}
                            />
                        </Grid>
                    )}
                    <Grid xs={12} sx={{marginBlock: -2}}/>
                    <Grid xs={12} md={6}>
                        <MuiRFF.DateTimePicker
                            name={"expiryAfter"}
                            label={t("expiryAfter")}
                            required={required.expiryAfter}
                            maxDateTime={values.expiryAfter}
                            textFieldProps={{
                                variant: "filled",
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <MuiRFF.DateTimePicker
                            name={"expiryBefore"}
                            label={t("expiryBefore")}
                            required={required.expiryBefore}
                            minDate={values.expiryAfter}
                            textFieldProps={{
                                variant: "filled",
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Grid>
                    <Grid xs={12} sx={{display: "flex", justifyContent: "center", gap: 1}}>
                        <Button type="submit" variant="contained" color="primary" size="large" sx={{minWidth: 200}}>
                            {t("submit")}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Form>
    );
};
